// src/HeadloProviderV1.tsx
import React from "react";

// src/pkce.ts
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// src/client-v1.ts
var VERIFIER_KEY = "headlo_pkce_verifier";
var USER_CACHE_KEY = "headlo_auth_user";
var REFRESH_LEAD_MS = 10 * 1e3;
var BROADCAST_CHANNEL = "headlo_auth";
var AUTH_CHANGE_EVENT = "headlo:auth:change";
function log(msg, data) {
  if (data) console.log(`%c[headlo-auth]%c ${msg}`, "color:#5dcaa5;font-weight:bold", "color:inherit", data);
  else console.log(`%c[headlo-auth]%c ${msg}`, "color:#5dcaa5;font-weight:bold", "color:inherit");
}
function fmtSeconds(ms) {
  const s = Math.floor(ms / 1e3);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor(s % 3600 / 60)}m`;
}
function decodeJwtPayload(jwt) {
  try {
    const [, payload] = jwt.split(".");
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}
function decodeJwtExp(jwt) {
  const c = decodeJwtPayload(jwt);
  return c && typeof c.exp === "number" ? c.exp * 1e3 : null;
}
function userFromJwt(jwt) {
  const c = decodeJwtPayload(jwt);
  if (!c?.sub || !c.email) return null;
  return { id: c.sub, email: c.email, displayName: c.name ?? null };
}
function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) {
      console.log("[headlo-auth] \u{1F4BE} No cached user \u2014 first paint will show Loading\u2026 until /oauth/refresh returns");
      return null;
    }
    const u = JSON.parse(raw);
    if (!u.id || !u.email) {
      console.log("[headlo-auth] \u{1F4BE} Cached user malformed \u2014 ignoring");
      return null;
    }
    console.log("[headlo-auth] \u{1F4BE} Hydrated from localStorage cache \u2014 signed-in UI paints instantly", { email: u.email, id: u.id });
    return u;
  } catch {
    return null;
  }
}
function writeCachedUser(u) {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    console.log("[headlo-auth] \u{1F4BE} Cached user profile \u2192 next reload will paint instantly", { email: u.email, id: u.id });
  } catch {
  }
}
function clearCachedUser() {
  try {
    if (localStorage.getItem(USER_CACHE_KEY)) {
      localStorage.removeItem(USER_CACHE_KEY);
      console.log("[headlo-auth] \u{1F4BE} Cleared cached user profile");
    }
  } catch {
  }
}
var HeadloAuthClientV1 = class {
  constructor(config) {
    this._token = null;
    this.inFlight = null;
    this.channel = null;
    this.listeners = /* @__PURE__ */ new Set();
    this.boundExternalChange = null;
    this.config = config;
    this.instanceId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    this._user = readCachedUser();
    this._isLoaded = this._user !== null;
  }
  async init() {
    log(`\u{1F680} HeadloAuthClientV1 init`, {
      issuer: this.config.issuer,
      publishableKey: this.config.publishableKey.slice(0, 12) + "\u2026"
    });
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      log(`\u{1F511} Found ?code= in URL \u2014 exchanging PKCE auth code for tokens`);
      await this.handleCallback(code);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("code");
      clean.searchParams.delete("state");
      window.history.replaceState({}, "", clean.toString());
    } else {
      log(`\u{1F50D} No PKCE code \u2014 trying silent refresh (will succeed if cookie present)`);
      await this.refreshAccessToken();
    }
    this._isLoaded = true;
    log(`\u2728 HeadloAuthClientV1 isLoaded=true`);
    this.emit();
    this.channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(BROADCAST_CHANNEL) : null;
    if (this.channel) {
      this.channel.onmessage = (e) => {
        if (e.data === "signout") {
          log(`\u{1F4E1} BroadcastChannel: another tab signed out \u2014 clearing local session`);
          this.clearSession();
        }
      };
    }
    if (typeof window !== "undefined") {
      this.boundExternalChange = (e) => this.handleExternalChange(e);
      window.addEventListener(AUTH_CHANGE_EVENT, this.boundExternalChange);
    }
  }
  destroy() {
    this.channel?.close();
    this.channel = null;
    if (this.boundExternalChange && typeof window !== "undefined") {
      window.removeEventListener(AUTH_CHANGE_EVENT, this.boundExternalChange);
      this.boundExternalChange = null;
    }
  }
  // ─── State readers ────────────────────────────────────────────────────
  getState() {
    return {
      isLoaded: this._isLoaded,
      isSignedIn: !!this._user,
      user: this._user,
      token: this._token
    };
  }
  getUser() {
    return this._user;
  }
  isSignedIn() {
    return !!this._user;
  }
  isLoaded() {
    return this._isLoaded;
  }
  onChange(cb) {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }
  // Emit local + dispatch cross-instance CustomEvent. Called from every
  // state mutation originating INSIDE this instance.
  emit() {
    const s = this.getState();
    this.notifyLocal(s);
    if (typeof window !== "undefined") {
      const detail = { version: "v1", ...s, sourceInstanceId: this.instanceId };
      window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT, { detail }));
    }
  }
  // Fire local subscribers only — no cross-instance dispatch. Used when
  // applying state that arrived FROM another instance's event.
  notifyLocal(s) {
    this.listeners.forEach((cb) => {
      try {
        cb(s);
      } catch (e) {
        console.error("[headlo-auth] listener threw:", e);
      }
    });
  }
  handleExternalChange(e) {
    const detail = e.detail;
    if (!detail || detail.sourceInstanceId === this.instanceId) return;
    if (detail.version !== "v1") return;
    if (this._token === detail.token && this._user?.id === detail.user?.id && this._isLoaded === detail.isLoaded) return;
    log(`\u{1F517} Cross-instance sync \u2014 applying state from ${detail.sourceInstanceId.slice(0, 8)}\u2026`, {
      signedIn: detail.isSignedIn,
      user: detail.user?.email ?? null
    });
    this._token = detail.token;
    this._user = detail.user;
    this._isLoaded = detail.isLoaded;
    this.notifyLocal(this.getState());
  }
  // ─── Public methods ───────────────────────────────────────────────────
  async getToken() {
    if (!this._token) {
      log(`\u{1F50D} getToken() \u2014 no token in memory, returning null`);
      return null;
    }
    const expMs = decodeJwtExp(this._token);
    if (expMs && expMs - Date.now() < REFRESH_LEAD_MS) {
      log(`\u{1F50D} getToken() \u2014 token has < ${fmtSeconds(REFRESH_LEAD_MS)} left, refreshing first`, {
        timeLeft: fmtSeconds(expMs - Date.now())
      });
      return this.refreshAccessToken();
    }
    log(`\u{1F50D} getToken() \u2014 returning cached token`, {
      timeLeft: expMs ? fmtSeconds(expMs - Date.now()) : "?"
    });
    return this._token;
  }
  async signIn(opts) {
    log(`\u{1F6AA} signIn() \u2014 generating PKCE + redirecting to /oauth/authorize`);
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    localStorage.setItem(VERIFIER_KEY, verifier);
    const url = new URL(`${this.config.issuer}/oauth/authorize`, window.location.origin);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.config.publishableKey);
    const target = opts?.forceRedirectUrl ?? this.config.signInForceRedirectUrl ?? this.config.signInFallbackRedirectUrl;
    const redirectUri = target ? window.location.origin + target : window.location.origin + window.location.pathname;
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("scope", "openid email profile");
    window.location.href = url.toString();
  }
  async signOut() {
    log(`\u{1F6AA} signOut() \u2014 POST /oauth/signout + clearing local session`);
    fetch(`${this.config.issuer}/oauth/signout`, {
      method: "POST",
      credentials: "include"
    }).catch(() => {
    });
    this.clearSession();
    log(`\u{1F4E1} Broadcasting signout to other tabs`);
    this.channel?.postMessage("signout");
  }
  // ─── Internals — moved verbatim from HeadloProvider ────────────────
  async refreshAccessToken() {
    if (this.inFlight) {
      log(`\u23F8\uFE0F  Refresh already in-flight \u2014 returning existing promise (concurrency guard)`);
      return this.inFlight;
    }
    log(`\u{1F504} POST /oauth/refresh \u2014 sending refresh cookie`);
    const startMs = Date.now();
    this.inFlight = (async () => {
      const res = await fetch(`${this.config.issuer}/oauth/refresh?client_id=${encodeURIComponent(this.config.publishableKey)}`, {
        method: "POST",
        credentials: "include"
        // send headlo_refresh HttpOnly cookie
      });
      const took = Date.now() - startMs;
      if (res.status === 204) {
        log(`\u{1F4A4} /oauth/refresh \u2192 204 (no cookie, anonymous) \u2014 staying signed out`, { tookMs: took });
        this.clearSession();
        return null;
      }
      if (!res.ok) {
        log(`\u274C /oauth/refresh \u2192 ${res.status} \u2014 session invalid, signing out`, { tookMs: took });
        this.clearSession();
        return null;
      }
      const { access_token } = await res.json();
      const expMs = decodeJwtExp(access_token);
      log(`\u2705 /oauth/refresh \u2192 200 \u2014 got new access token`, {
        tookMs: took,
        newTokenLife: expMs ? fmtSeconds(expMs - Date.now()) : "?",
        newExpAt: expMs ? new Date(expMs).toLocaleTimeString() : "?"
      });
      this.setTokenAndUser(access_token);
      return access_token;
    })();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }
  async handleCallback(code) {
    const verifier = localStorage.getItem(VERIFIER_KEY);
    if (!verifier) return;
    localStorage.removeItem(VERIFIER_KEY);
    const res = await fetch(`${this.config.issuer}/oauth/token`, {
      method: "POST",
      credentials: "include",
      // accept Set-Cookie for headlo_refresh
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: window.location.origin + window.location.pathname,
        client_id: this.config.publishableKey,
        code_verifier: verifier
      })
    });
    if (!res.ok) return;
    const { access_token } = await res.json();
    const expMs = decodeJwtExp(access_token);
    log(`\u2705 PKCE exchange complete \u2014 session created`, {
      tokenLife: expMs ? fmtSeconds(expMs - Date.now()) : "?",
      expAt: expMs ? new Date(expMs).toLocaleTimeString() : "?"
    });
    this.setTokenAndUser(access_token);
  }
  // Single write path for token + user cache. Emits state change to subscribers.
  setTokenAndUser(token) {
    this._token = token;
    const u = userFromJwt(token);
    if (u) {
      if (!this._user) log(`\u{1F464} Hydrated user from JWT claims`, { email: u.email, id: u.id });
      this._user = u;
      writeCachedUser(u);
    }
    this.emit();
  }
  clearSession() {
    log(`\u{1F9F9} Session cleared \u2014 user signed out (in this tab)`);
    this._token = null;
    this._user = null;
    clearCachedUser();
    this.emit();
  }
};

// src/HeadloProviderV1.tsx
import { jsx } from "react/jsx-runtime";
var DEFAULT_ISSUER = "https://auth.headlo.com";
var HEADLO_AUTH_VERSION = "v1-extract";
console.log(`%c[headlo-auth]%c \u{1F4E6} SDK build: ${HEADLO_AUTH_VERSION}`, "color:#5dcaa5;font-weight:bold", "color:inherit");
var HeadloAuthContext = React.createContext(null);
function HeadloProviderV1({
  publishableKey,
  issuer = DEFAULT_ISSUER,
  signInForceRedirectUrl,
  signInFallbackRedirectUrl,
  signUpForceRedirectUrl: _signUpForce,
  signUpFallbackRedirectUrl: _signUpFallback,
  children
}) {
  const clientRef = React.useRef(null);
  if (!clientRef.current) {
    clientRef.current = new HeadloAuthClientV1({
      publishableKey,
      issuer,
      signInForceRedirectUrl,
      signInFallbackRedirectUrl
    });
  }
  const client = clientRef.current;
  const [state, setState] = React.useState(() => client.getState());
  React.useEffect(() => {
    const unsubscribe = client.onChange(setState);
    void client.init();
    return () => {
      unsubscribe();
      client.destroy();
    };
  }, []);
  const value = React.useMemo(() => ({
    isLoaded: state.isLoaded,
    isSignedIn: state.isSignedIn,
    user: state.user,
    getToken: () => client.getToken(),
    signIn: (opts) => client.signIn(opts),
    signOut: () => client.signOut(),
    publishableKey
  }), [state, client, publishableKey]);
  return /* @__PURE__ */ jsx(HeadloAuthContext.Provider, { value, children });
}

// src/HeadloProviderV2.tsx
import React2 from "react";

// src/idb.ts
var DB_NAME = "headlo_auth_v2";
var STORE_NAME = "dpop_keys";
var DB_VERSION = 1;
var dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable \u2014 cannot use HeadloProviderV2"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
  return dbPromise;
}
async function idbPut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB put failed"));
  });
}
async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB get failed"));
  });
}
async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB delete failed"));
  });
}

// src/dpop.ts
var LEGACY_KEY_STORAGE_ID = "session_key";
var keyStorageIdFor = (publishableKey) => publishableKey ? `${LEGACY_KEY_STORAGE_ID}:${publishableKey}` : LEGACY_KEY_STORAGE_ID;
function base64url(input) {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input instanceof Uint8Array ? input : new Uint8Array(input);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function generateKeyPair() {
  return crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    // extractable — private key is NOT extractable
    ["sign", "verify"]
  );
}
async function getOrCreateKeyPair(publishableKey) {
  const existing = await getExistingKeyPair(publishableKey);
  if (existing) return existing;
  const fresh = await generateKeyPair();
  await idbPut(keyStorageIdFor(publishableKey), fresh);
  return fresh;
}
async function getExistingKeyPair(publishableKey) {
  const scopedId = keyStorageIdFor(publishableKey);
  const scoped = await idbGet(scopedId);
  if (scoped?.privateKey && scoped?.publicKey) return scoped;
  if (publishableKey) {
    const legacy = await idbGet(LEGACY_KEY_STORAGE_ID);
    if (legacy?.privateKey && legacy?.publicKey) {
      await idbPut(scopedId, legacy);
      await idbDelete(LEGACY_KEY_STORAGE_ID);
      return legacy;
    }
  }
  return null;
}
async function clearKeyPair(publishableKey) {
  await idbDelete(keyStorageIdFor(publishableKey));
}
async function exportPublicJwk(keyPair) {
  return crypto.subtle.exportKey("jwk", keyPair.publicKey);
}
async function computeJwkThumbprint(jwk) {
  const canonical = JSON.stringify({
    crv: jwk.crv,
    kty: jwk.kty,
    x: jwk.x,
    y: jwk.y
  });
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical));
  return base64url(digest);
}
async function signDpopProof(keyPair, opts) {
  const publicJwk = await exportPublicJwk(keyPair);
  const header = {
    alg: "ES256",
    typ: "dpop+jwt",
    jwk: publicJwk
  };
  const payload = {
    htu: opts.url,
    htm: opts.method.toUpperCase(),
    iat: Math.floor(Date.now() / 1e3),
    jti: crypto.randomUUID()
  };
  if (opts.nonce) payload.nonce = opts.nonce;
  const signingInput = base64url(JSON.stringify(header)) + "." + base64url(JSON.stringify(payload));
  const signatureBuf = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    keyPair.privateKey,
    new TextEncoder().encode(signingInput)
  );
  return signingInput + "." + base64url(signatureBuf);
}
async function dpopFetch(url, opts) {
  const method = (opts.method ?? "GET").toUpperCase();
  const proof = await signDpopProof(opts.keyPair, { url, method, nonce: opts.nonce });
  const headers = new Headers(opts.headers ?? {});
  headers.set("DPoP", proof);
  if (opts.accessToken) headers.set("Authorization", `Bearer ${opts.accessToken}`);
  const { keyPair: _kp, accessToken: _at, nonce: _n, ...init } = opts;
  return fetch(url, { ...init, method, headers });
}

// src/client-v2.ts
var VERIFIER_KEY2 = "headlo_pkce_verifier";
var USER_CACHE_KEY2 = "headlo_auth_user";
var SESSION_TOKEN_KEY = "headlo_auth_v2_token";
var REFRESH_LEAD_MS2 = 10 * 1e3;
var BROADCAST_CHANNEL2 = "headlo_auth";
var DEBUG_LOG_KEY = "headlo_auth_v2_debug_log";
var DEBUG_LOG_MAX = 200;
function persistLog(entry) {
  try {
    const raw = localStorage.getItem(DEBUG_LOG_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(entry);
    const trimmed = arr.length > DEBUG_LOG_MAX ? arr.slice(-DEBUG_LOG_MAX) : arr;
    localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(trimmed));
  } catch {
  }
}
function log2(msg, data) {
  if (data) console.log(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit", data);
  else console.log(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit");
  persistLog({ ts: Date.now(), msg, data });
}
function logExternal(msg, data) {
  persistLog({ ts: Date.now(), msg, data });
}
function decodeJwtPayload2(jwt) {
  try {
    const [, payload] = jwt.split(".");
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}
function decodeJwtExp2(jwt) {
  const c = decodeJwtPayload2(jwt);
  return c && typeof c.exp === "number" ? c.exp * 1e3 : null;
}
function decodeJwtCnfJkt(jwt) {
  const c = decodeJwtPayload2(jwt);
  return c?.cnf?.jkt ?? null;
}
function userFromJwt2(jwt) {
  const c = decodeJwtPayload2(jwt);
  if (!c?.sub || !c.email) return null;
  return { id: c.sub, email: c.email, displayName: c.name ?? null };
}
function readCachedUser2() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY2);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u.id && u.email ? u : null;
  } catch {
    return null;
  }
}
function writeCachedUser2(u) {
  try {
    localStorage.setItem(USER_CACHE_KEY2, JSON.stringify(u));
  } catch {
  }
}
function clearCachedUser2() {
  try {
    localStorage.removeItem(USER_CACHE_KEY2);
  } catch {
  }
}
function readSessionToken() {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}
function writeSessionToken(token) {
  try {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    const readback = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (readback !== token) {
      const msg = `\u26A0\uFE0F sessionStorage write verify FAILED \u2014 write:${token.length}b readback:${readback?.length ?? "null"}`;
      console.warn(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit");
      logExternal(msg);
    } else {
      const msg = `\u{1F4BE} Wrote + verified sessionStorage (${token.length}b, key=${SESSION_TOKEN_KEY})`;
      console.log(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit");
      logExternal(msg);
    }
  } catch (e) {
    const msg = `\u{1F4A5} sessionStorage.setItem THREW: ${String(e)}`;
    console.error(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit", e);
    logExternal(msg);
  }
}
function clearSessionToken() {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    const msg = `\u{1F5D1}\uFE0F Cleared sessionStorage token`;
    console.log(`%c[headlo-auth-v2]%c ${msg}`, "color:#7c3aed;font-weight:bold", "color:inherit");
    logExternal(msg);
  } catch {
  }
}
var AUTH_CHANGE_EVENT2 = "headlo:auth:change";
var HeadloAuthClientV2 = class {
  constructor(config) {
    this.inFlight = null;
    this.channel = null;
    this.listeners = /* @__PURE__ */ new Set();
    // Platform-adapter callback subscription. Populated in init() when config
    // has a platformAdapter; cleared in destroy(). Guarded against
    // re-subscription so repeated init() calls don't stack listeners.
    this._adapterUnsubscribe = null;
    // Bound handler so add/removeEventListener see the same reference.
    this.boundExternalChange = null;
    this.config = config;
    this.instanceId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    this._token = readSessionToken();
    this._user = this._token ? readCachedUser2() : null;
    this._isLoaded = this._token !== null && this._user !== null;
  }
  // Kick off async init. Provider calls this in a useEffect; vanilla
  // consumers call after `new`. Idempotent — repeated calls are cheap.
  async init() {
    log2(`\u{1F680} HeadloAuthClientV2 init`, {
      issuer: this.config.issuer,
      publishableKey: this.config.publishableKey.slice(0, 12) + "\u2026",
      platform: this.config.platformAdapter ? "adapter" : "web"
    });
    if (this.config.platformAdapter) {
      if (!this._adapterUnsubscribe) {
        this._adapterUnsubscribe = this.config.platformAdapter.subscribeToCallback(({ code: code2 }) => {
          log2(`\u{1F511} Adapter callback \u2014 exchanging PKCE + registering DPoP key`);
          void this.handleCallback(code2);
        });
      }
    }
    const code = this.config.platformAdapter ? null : new URLSearchParams(window.location.search).get("code");
    if (code) {
      log2(`\u{1F511} Found ?code= in URL \u2014 exchanging PKCE + registering DPoP key`);
      await this.handleCallback(code);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("code");
      clean.searchParams.delete("state");
      window.history.replaceState({}, "", clean.toString());
    } else if (this._token) {
      const cnfJkt = decodeJwtCnfJkt(this._token);
      if (cnfJkt) {
        const keyPair = await getExistingKeyPair(this.config.publishableKey);
        if (!keyPair) {
          log2(`\u26A0\uFE0F  Cached token references missing DPoP key (jkt=${cnfJkt.slice(0, 8)}\u2026) \u2014 clearing session`);
          this.clearSession();
          this._isLoaded = true;
          this.emit();
          return;
        }
        try {
          const jwk = await exportPublicJwk(keyPair);
          const storedJkt = await computeJwkThumbprint(jwk);
          if (storedJkt !== cnfJkt) {
            log2(`\u26A0\uFE0F  Cached token cnf.jkt=${cnfJkt.slice(0, 8)}\u2026 doesn't match IndexedDB key jkt=${storedJkt.slice(0, 8)}\u2026 \u2014 clearing session`);
            this.clearSession();
            this._isLoaded = true;
            this.emit();
            return;
          }
        } catch (e) {
          log2(`\u26A0\uFE0F  Could not compute stored key thumbprint \u2014 clearing session as precaution`, { error: String(e) });
          this.clearSession();
          this._isLoaded = true;
          this.emit();
          return;
        }
      }
      const expMs = decodeJwtExp2(this._token);
      if (expMs && expMs - Date.now() > REFRESH_LEAD_MS2) {
        const secLeft = Math.floor((expMs - Date.now()) / 1e3);
        log2(`\u267B\uFE0F  Token still fresh (${secLeft}s left) \u2014 using cached, skipping refresh`);
      } else {
        log2(`\u267B\uFE0F  Token near/past expiry \u2014 validating with silent refresh`);
        await this.refreshAccessToken();
      }
    } else {
      log2(`\u{1F4A4} No cached token \u2014 attempting resurrection from DPoP key`);
      await this.mintFromDpopKey();
    }
    this._isLoaded = true;
    this.emit();
    this.channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(BROADCAST_CHANNEL2) : null;
    if (this.channel) {
      this.channel.onmessage = (e) => {
        const data = e.data;
        if (data === "signout") {
          log2(`\u{1F4E1} BroadcastChannel: legacy signout from another tab \u2014 clearing session`);
          this.clearSession();
        } else if (data && typeof data === "object" && data.type === "signout" && data.pk === this.config.publishableKey) {
          log2(`\u{1F4E1} BroadcastChannel: signout for pk=${data.pk} from another tab`);
          this.clearSession();
        }
      };
    }
    if (typeof window !== "undefined") {
      this.boundExternalChange = (e) => this.handleExternalChange(e);
      window.addEventListener(AUTH_CHANGE_EVENT2, this.boundExternalChange);
    }
  }
  // Cleanup — close BroadcastChannel + unhook the cross-instance listener.
  // Idempotent.
  destroy() {
    this.channel?.close();
    this.channel = null;
    if (this.boundExternalChange && typeof window !== "undefined") {
      window.removeEventListener(AUTH_CHANGE_EVENT2, this.boundExternalChange);
      this.boundExternalChange = null;
    }
    if (this._adapterUnsubscribe) {
      this._adapterUnsubscribe();
      this._adapterUnsubscribe = null;
    }
  }
  // ─── State readers ────────────────────────────────────────────────────
  getState() {
    return {
      isLoaded: this._isLoaded,
      isSignedIn: !!this._user,
      user: this._user,
      token: this._token
    };
  }
  getUser() {
    return this._user;
  }
  isSignedIn() {
    return !!this._user;
  }
  isLoaded() {
    return this._isLoaded;
  }
  onChange(cb) {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }
  // Emit local + dispatch cross-instance CustomEvent. Called from every
  // state mutation that originated INSIDE this instance (sign-in, refresh,
  // sign-out, mint, clearSession). External-origin changes route through
  // notifyLocal() only — never dispatch, to prevent loops.
  emit() {
    const s = this.getState();
    this.notifyLocal(s);
    if (typeof window !== "undefined") {
      const detail = { version: "v2", ...s, sourceInstanceId: this.instanceId };
      window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT2, { detail }));
    }
  }
  // Fire local subscribers only — no cross-instance dispatch. Used when
  // applying state that arrived FROM another instance's event (we already
  // know about it; re-dispatching would ping-pong forever).
  notifyLocal(s) {
    this.listeners.forEach((cb) => {
      try {
        cb(s);
      } catch (e) {
        console.error("[headlo-auth-v2] listener threw:", e);
      }
    });
  }
  // Cross-instance event handler. Skip our own emissions (sourceInstanceId
  // match), otherwise apply the external state to our in-memory fields and
  // notify local subscribers. Does NOT re-dispatch — that's what breaks the
  // loop.
  handleExternalChange(e) {
    const detail = e.detail;
    if (!detail || detail.sourceInstanceId === this.instanceId) return;
    if (detail.version !== "v2") return;
    if (this._token === detail.token && this._user?.id === detail.user?.id && this._isLoaded === detail.isLoaded) return;
    log2(`\u{1F517} Cross-instance sync \u2014 applying state from ${detail.sourceInstanceId.slice(0, 8)}\u2026`, {
      signedIn: detail.isSignedIn,
      user: detail.user?.email ?? null
    });
    this._token = detail.token;
    this._user = detail.user;
    this._isLoaded = detail.isLoaded;
    this.notifyLocal(this.getState());
  }
  // ─── Public methods ───────────────────────────────────────────────────
  async getToken() {
    if (!this._token) return null;
    const expMs = decodeJwtExp2(this._token);
    if (expMs && expMs - Date.now() < REFRESH_LEAD_MS2) {
      log2(`\u{1F50D} getToken() \u2014 token expiring soon, refreshing first`);
      return this.refreshAccessToken();
    }
    return this._token;
  }
  // Authenticated fetch with a fresh DPoP proof per call. Consumers hit
  // any Headlo backend endpoint that expects Authorization: Bearer + DPoP
  // (all v2-scoped MCP tools, /oauth/dpop/* endpoints, DPoP-enforced data
  // API endpoints per A.5.1) through this helper — no need to touch
  // signDpopProof / dpopFetch primitives directly.
  //
  // Retrieves the IndexedDB-stored extractable:false keypair, gets a fresh
  // access token (auto-refreshes if near expiry), signs a per-request proof
  // for (method, url), and dispatches. Throws if not signed in or if the
  // DPoP key is missing (session was signed out but token cache is stale).
  //
  // On 401, force-refreshes the token and retries once. Closes the narrow
  // stale-token race where getToken()'s REFRESH_LEAD_MS margin + clock skew
  // + network latency lets a squeaking-past-expiry token slip out. Retry
  // only fires when the server disagrees with our expiry view — a legitimate
  // 401 (revoked session, wrong scope) is surfaced after refresh returns the
  // same 401 or refresh itself fails.
  async dpopFetch(url, init = {}) {
    const keyPair = await getExistingKeyPair(this.config.publishableKey);
    if (!keyPair) throw new Error("no DPoP key in IndexedDB \u2014 session may have been signed out");
    const method = (init.method ?? "GET").toUpperCase();
    const headers = init.headers;
    const body = init.body;
    const dispatch = async (token) => dpopFetch(url, { method, keyPair, accessToken: token, headers, body });
    const firstToken = await this.getToken();
    if (!firstToken) throw new Error("not signed in \u2014 call signIn() first");
    const res = await dispatch(firstToken);
    if (res.status === 401) {
      log2(`\u{1F501} dpopFetch 401 \u2014 forcing refresh + retry`, { url });
      const refreshed = await this.refreshAccessToken();
      if (refreshed && refreshed !== firstToken) {
        return dispatch(refreshed);
      }
      log2(`\u26A0\uFE0F dpopFetch retry skipped \u2014 refresh returned ${refreshed === null ? "null" : "same token"}`);
    }
    return res;
  }
  async signIn(opts) {
    log2(`\u{1F6AA} signIn() \u2014 generating PKCE + ${this.config.platformAdapter ? "opening system browser" : "redirecting"}`);
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    localStorage.setItem(VERIFIER_KEY2, verifier);
    const redirectUri = this.config.platformAdapter ? this.config.platformAdapter.redirectUri : (() => {
      const target = opts?.forceRedirectUrl ?? this.config.signInForceRedirectUrl ?? this.config.signInFallbackRedirectUrl;
      return target ? window.location.origin + target : window.location.origin + window.location.pathname;
    })();
    const authorizeBase = this.config.platformAdapter ? this.config.issuer : window.location.origin;
    const url = new URL(`${this.config.issuer}/oauth/authorize`, authorizeBase);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.config.publishableKey);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("scope", "openid email profile");
    if (this.config.platformAdapter) {
      await this.config.platformAdapter.openAuthUrl(url.toString());
    } else {
      window.location.href = url.toString();
    }
  }
  async signOut() {
    log2(`\u{1F6AA} signOut() \u2014 revoking DPoP + clearing local state`);
    const currentToken = this._token;
    if (currentToken) {
      const keyPair = await getOrCreateKeyPair(this.config.publishableKey).catch(() => null);
      if (keyPair) {
        const revokeUrl = `${this.config.issuer}/oauth/dpop/revoke`;
        dpopFetch(revokeUrl, {
          method: "POST",
          keyPair,
          accessToken: currentToken,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ client_id: this.config.publishableKey })
        }).catch(() => {
        });
      }
    }
    this.clearSession();
    this.channel?.postMessage({ type: "signout", pk: this.config.publishableKey });
  }
  // ─── Internals — moved verbatim from HeadloProviderV2 ─────────────────
  async handleCallback(code) {
    const verifier = localStorage.getItem(VERIFIER_KEY2);
    if (!verifier) return;
    localStorage.removeItem(VERIFIER_KEY2);
    const redirectUri = this.config.platformAdapter ? this.config.platformAdapter.redirectUri : window.location.origin + window.location.pathname;
    const tokenRes = await fetch(`${this.config.issuer}/oauth/token`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: redirectUri,
        client_id: this.config.publishableKey,
        code_verifier: verifier
      })
    });
    if (!tokenRes.ok) {
      log2(`\u274C PKCE token exchange failed`, { status: tokenRes.status });
      return;
    }
    const { access_token: bootstrapToken } = await tokenRes.json();
    log2(`\u2705 PKCE exchange complete \u2014 bootstrap access token issued`);
    log2(`\u{1F510} Registering DPoP key with /oauth/dpop/register`);
    const keyPair = await getOrCreateKeyPair(this.config.publishableKey);
    const registerUrl = `${this.config.issuer}/oauth/dpop/register`;
    const regRes = await dpopFetch(registerUrl, {
      method: "POST",
      keyPair,
      accessToken: bootstrapToken,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_id: this.config.publishableKey })
    });
    if (!regRes.ok) {
      log2(`\u274C DPoP registration failed`, { status: regRes.status });
      return;
    }
    const { access_token: dpopToken } = await regRes.json();
    const expMs = decodeJwtExp2(dpopToken);
    log2(`\u2705 DPoP registered \u2014 session bound to key`, {
      tokenLife: expMs ? `${Math.floor((expMs - Date.now()) / 1e3)}s` : "?"
    });
    this.setTokenAndUser(dpopToken);
  }
  async refreshAccessToken() {
    if (this.inFlight) {
      log2(`\u23F8\uFE0F  Refresh in-flight \u2014 returning existing promise`);
      return this.inFlight;
    }
    const currentToken = this._token;
    if (!currentToken) {
      log2(`\u{1F4A4} No access token to refresh \u2014 user must sign in explicitly`);
      return null;
    }
    log2(`\u{1F504} POST /oauth/token/refresh-dpop`);
    const startMs = Date.now();
    this.inFlight = (async () => {
      try {
        const keyPair = await getOrCreateKeyPair(this.config.publishableKey);
        const url = `${this.config.issuer}/oauth/token/refresh-dpop`;
        const res = await dpopFetch(url, {
          method: "POST",
          keyPair,
          accessToken: currentToken,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ client_id: this.config.publishableKey })
        });
        const took = Date.now() - startMs;
        if (res.status === 401 || res.status === 403) {
          const body = await res.json().catch(() => null);
          const errCode = typeof body?.error === "string" ? body.error : "";
          if (errCode === "DPOP_KEY_REVOKED") {
            log2(`\u274C /oauth/token/refresh-dpop \u2192 ${res.status} DPOP_KEY_REVOKED \u2014 clearing local`, { tookMs: took });
            this.clearSession();
            return null;
          }
          log2(`\u26A0\uFE0F /oauth/token/refresh-dpop \u2192 ${res.status} ${errCode || "unspecified"} \u2014 keeping session (refresh failed but token cache preserved)`, { tookMs: took });
          return null;
        }
        if (!res.ok) {
          log2(`\u26A0\uFE0F /oauth/token/refresh-dpop \u2192 ${res.status} \u2014 transient, keeping session`, { tookMs: took });
          return null;
        }
        const { access_token } = await res.json();
        const expMs = decodeJwtExp2(access_token);
        log2(`\u2705 /oauth/token/refresh-dpop \u2192 200`, {
          tookMs: took,
          tokenLife: expMs ? `${Math.floor((expMs - Date.now()) / 1e3)}s` : "?"
        });
        this.setTokenAndUser(access_token);
        return access_token;
      } catch (err) {
        log2(`\u26A0\uFE0F Refresh network error \u2014 keeping session (may be page navigation)`, { error: String(err) });
        return null;
      }
    })();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }
  async mintFromDpopKey() {
    log2(`\u{1F52E} Attempting resurrection via /oauth/dpop/mint`);
    const startMs = Date.now();
    const keyPair = await getExistingKeyPair(this.config.publishableKey);
    if (!keyPair) {
      log2(`\u{1F4A4} No DPoP key in IndexedDB \u2014 nothing to resurrect`);
      return null;
    }
    try {
      const url = `${this.config.issuer}/oauth/dpop/mint`;
      const res = await dpopFetch(url, {
        method: "POST",
        keyPair,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: this.config.publishableKey })
      });
      const took = Date.now() - startMs;
      if (res.status === 401 || res.status === 403) {
        const body = await res.json().catch(() => null);
        const errCode = typeof body?.error === "string" ? body.error : "";
        if (errCode === "DPOP_KEY_REVOKED") {
          log2(`\u274C /oauth/dpop/mint \u2192 ${res.status} DPOP_KEY_REVOKED \u2014 clearing local`, { tookMs: took });
          this.clearSession();
          return null;
        }
        log2(`\u26A0\uFE0F /oauth/dpop/mint \u2192 ${res.status} ${errCode || "unspecified"} \u2014 keeping session`, { tookMs: took });
        return null;
      }
      if (!res.ok) {
        log2(`\u26A0\uFE0F /oauth/dpop/mint \u2192 ${res.status} \u2014 transient, keeping key`, { tookMs: took });
        return null;
      }
      const { access_token } = await res.json();
      const expMs = decodeJwtExp2(access_token);
      log2(`\u2705 /oauth/dpop/mint \u2192 200 \u2014 session resurrected`, {
        tookMs: took,
        tokenLife: expMs ? `${Math.floor((expMs - Date.now()) / 1e3)}s` : "?"
      });
      this.setTokenAndUser(access_token);
      return access_token;
    } catch (err) {
      log2(`\u26A0\uFE0F Mint network error \u2014 keeping key (may be page navigation)`, { error: String(err) });
      return null;
    }
  }
  // Single write path for token + user cache — used by handleCallback,
  // refreshAccessToken, mintFromDpopKey. Emits state change to subscribers.
  setTokenAndUser(token) {
    this._token = token;
    writeSessionToken(token);
    const u = userFromJwt2(token);
    if (u) {
      this._user = u;
      writeCachedUser2(u);
    }
    this.emit();
  }
  clearSession() {
    const stack = new Error().stack?.split("\n").slice(1, 5).join(" | ") ?? "?";
    console.trace("[headlo-auth-v2] clearSession() called \u2014 stack trace");
    log2(`\u{1F9F9} Session cleared \u2014 caller: ${stack}`);
    this._token = null;
    this._user = null;
    clearSessionToken();
    clearCachedUser2();
    void clearKeyPair(this.config.publishableKey);
    void clearKeyPair();
    this.emit();
  }
};

// src/HeadloProviderV2.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var DEFAULT_ISSUER2 = "https://auth.headlo.com";
var HEADLO_AUTH_V2_VERSION = "v2-abs-phase-1-extract";
console.log(`%c[headlo-auth-v2]%c \u{1F4E6} SDK build: ${HEADLO_AUTH_V2_VERSION}`, "color:#7c3aed;font-weight:bold", "color:inherit");
function HeadloProviderV2({
  publishableKey,
  issuer = DEFAULT_ISSUER2,
  signInForceRedirectUrl,
  signInFallbackRedirectUrl,
  platformAdapter,
  children
}) {
  const clientRef = React2.useRef(null);
  if (!clientRef.current) {
    clientRef.current = new HeadloAuthClientV2({
      publishableKey,
      issuer,
      signInForceRedirectUrl,
      signInFallbackRedirectUrl,
      platformAdapter
    });
  }
  const client = clientRef.current;
  const [state, setState] = React2.useState(() => client.getState());
  React2.useEffect(() => {
    const unsubscribe = client.onChange(setState);
    void client.init();
    return () => {
      unsubscribe();
      client.destroy();
    };
  }, []);
  React2.useEffect(() => {
    const w = window;
    if (w.__headlo_service_headlo_auth_v2) return;
    const shim = {
      authFetch: async (args) => {
        const url = args.url;
        const opts = args.options ?? {};
        let res;
        try {
          res = state.isSignedIn ? await client.dpopFetch(url, opts) : await fetch(url, opts);
        } catch {
          res = await fetch(url, opts);
        }
        let body = null;
        const text = await res.text();
        try {
          body = JSON.parse(text);
        } catch {
          body = text;
        }
        const headers = {};
        res.headers.forEach((v, k) => {
          headers[k] = v;
        });
        return { status: res.status, ok: res.ok, body, headers };
      }
    };
    w.__headlo_service_headlo_auth_v2 = shim;
    return () => {
      if (w.__headlo_service_headlo_auth_v2 === shim) delete w.__headlo_service_headlo_auth_v2;
    };
  }, [client, state.isSignedIn]);
  const value = React2.useMemo(() => ({
    isLoaded: state.isLoaded,
    isSignedIn: state.isSignedIn,
    user: state.user,
    getToken: () => client.getToken(),
    signIn: (opts) => client.signIn(opts),
    signOut: () => client.signOut(),
    publishableKey
  }), [state, client, publishableKey]);
  return /* @__PURE__ */ jsx2(HeadloAuthContext.Provider, { value, children });
}

// src/hooks.ts
import React3 from "react";
function useHeadloAuthContext() {
  const ctx = React3.useContext(HeadloAuthContext);
  if (!ctx) throw new Error("useAuth / useHeadloAuth must be used inside <HeadloProvider>");
  return ctx;
}
function useAuth() {
  const { isLoaded, isSignedIn, getToken, signIn, signOut, publishableKey } = useHeadloAuthContext();
  return { isLoaded, isSignedIn, getToken, signIn, signOut, publishableKey };
}
function useUser() {
  return useHeadloAuthContext().user;
}
var useHeadloAuth = useAuth;
var useHeadloUser = useUser;

// src/components.tsx
import React4 from "react";
import { Fragment, jsx as jsx3 } from "react/jsx-runtime";
function SignInButton({
  children,
  forceRedirectUrl,
  ...props
}) {
  const { signIn } = useHeadloAuth();
  const handler = () => signIn({ forceRedirectUrl });
  if (React4.isValidElement(children)) {
    return React4.cloneElement(children, {
      onClick: handler
    });
  }
  return /* @__PURE__ */ jsx3("button", { onClick: handler, ...props, children: children ?? "Sign in" });
}
function SignOutButton({ children, ...props }) {
  const { signOut } = useHeadloAuth();
  if (React4.isValidElement(children)) {
    return React4.cloneElement(children, {
      onClick: () => signOut()
    });
  }
  return /* @__PURE__ */ jsx3("button", { onClick: () => signOut(), ...props, children: children ?? "Sign out" });
}
function SignedIn({ children }) {
  const { isLoaded, isSignedIn } = useHeadloAuth();
  if (!isLoaded || !isSignedIn) return null;
  return /* @__PURE__ */ jsx3(Fragment, { children });
}
function SignedOut({ children }) {
  const { isLoaded, isSignedIn } = useHeadloAuth();
  if (!isLoaded || isSignedIn) return null;
  return /* @__PURE__ */ jsx3(Fragment, { children });
}

// src/authFetch.ts
import React5 from "react";
function createAuthFetch(getToken) {
  return async (input, init) => {
    const token = await getToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  };
}
function useAuthFetch() {
  const { getToken } = useHeadloAuth();
  return React5.useMemo(() => createAuthFetch(getToken), [getToken]);
}
export {
  HeadloAuthClientV1,
  HeadloAuthClientV2,
  HeadloProviderV1,
  HeadloProviderV2,
  SignInButton,
  SignOutButton,
  SignedIn,
  SignedOut,
  createAuthFetch,
  dpopFetch,
  getExistingKeyPair,
  getOrCreateKeyPair,
  signDpopProof,
  useAuth,
  useAuthFetch,
  useHeadloAuth,
  useHeadloUser,
  useUser
};
